import { useEffect, useMemo, useState } from "react";
import { ApiError } from "../api/client";
import { extractPhoneDigits, formatPhoneDisplay } from "../utils/phone";
import { useDirectoryBundle, clearDirectoryCache } from "../api/directory";
import {
  CohortFilterBar,
  type CohortFilterValue,
} from "../components/cohorts/CohortFilterBar";
import { BranchFilterSelect } from "../components/common/BranchFilterSelect";
import { usePlatformBranchFilter } from "../hooks/usePlatformBranchFilter";
import {
  AutoPlanInstructorPriority,
  AutoPlanLessonType,
  PlannedAppointment,
  PreviewResponse,
  RunDetail,
  UnscheduledItem,
  commitAutoPlan,
  fetchRunDetail,
  previewAutoPlan,
  revertAutoPlan,
  useAutoPlanRunList,
} from "../api/autoPlanning";
import { UI_LOADING } from "../constants/uiLabels";
import "./AutoPlanningPage.css";

const WEEKDAY_LABELS = ["Pzt", "Sal", "Car", "Per", "Cum", "Cmt", "Paz"];

type Step = "configure" | "preview" | "history";

const REASON_LABELS: Record<UnscheduledItem["reason"], string> = {
  no_available_slot: "UyGün ders saati bulunamadi",
  student_not_active: "Kursiyer aktif degil",
  no_instructor_pool: "Aktif egitmen yok",
  no_vehicle_pool: "Aktif arac yok",
};

function formatIsoDateTime(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
}

function formatIsoDate(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("tr-TR", { dateStyle: "medium" });
}

function defaultStartDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

function defaultEndDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
}

const EMPTY_COHORT: CohortFilterValue = { periodId: "", groupId: "", subgroupId: "" };

export function AutoPlanningPage() {
  const [cohortFilter, setCohortFilter] = useState<CohortFilterValue>(EMPTY_COHORT);
  const { isPlatformOwner, branchFilter, effectiveBranchId, setBranchFilter, branches } =
    usePlatformBranchFilter();
  const directoryBranchId = effectiveBranchId || undefined;
  const directory = useDirectoryBundle(directoryBranchId, {
    trainingPeriodId: cohortFilter.periodId || undefined,
    trainingGroupId: cohortFilter.groupId || undefined,
    trainingSubgroupId: cohortFilter.subgroupId || undefined,
  });
  const [step, setStep] = useState<Step>("configure");
  const [studentSearch, setStudentSearch] = useState("");
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());

  const [startDate, setStartDate] = useState(defaultStartDate());
  const [endDate, setEndDate] = useState(defaultEndDate());
  const [lessonsPerStudent, setLessonsPerStudent] = useState(14);
  const [maxPerDay, setMaxPerDay] = useState(2);
  const [lessonType, setLessonType] = useState<AutoPlanLessonType>("normal");
  const [priority, setPriority] = useState<AutoPlanInstructorPriority>("full_time_first");
  const [excludeWeekdays, setExcludeWeekdays] = useState<number[]>([7]);
  const [preferredInstructorId, setPreferredInstructorId] = useState<string>("");
  const [preferredVehicleId, setPreferredVehicleId] = useState<string>("");

  const [busy, setBusy] = useState(false);
  const [previewResult, setPreviewResult] = useState<PreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const [historyDetail, setHistoryDetail] = useState<RunDetail | null>(null);
  const runList = useAutoPlanRunList({
    limit: 20,
    branchId: isPlatformOwner ? directoryBranchId : undefined,
  });

  useEffect(() => {
    setSelectedStudents(new Set());
    setPreviewResult(null);
  }, [effectiveBranchId]);

  const filteredStudents = useMemo(() => {
    const students = directory.data?.students || [];
    const term = studentSearch.trim().toLowerCase();
    if (!term) return students;
    const termDigits = extractPhoneDigits(term);
    return students.filter((s) => {
      if (s.full_name.toLowerCase().includes(term)) return true;
      if (!s.phone) return false;
      const display = formatPhoneDisplay(s.phone);
      if (display.includes(term)) return true;
      if (termDigits) return extractPhoneDigits(s.phone).includes(termDigits);
      return (s.phone || "").includes(term);
    });
  }, [directory.data, studentSearch]);

  const toggleStudent = (id: string) => {
    setSelectedStudents((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelectedStudents((prev) => {
      const next = new Set(prev);
      const allSelected = filteredStudents.every((s) => next.has(s.id));
      if (allSelected) {
        for (const s of filteredStudents) next.delete(s.id);
      } else {
        for (const s of filteredStudents) next.add(s.id);
      }
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedStudents((prev) => {
      const next = new Set(prev);
      for (const s of filteredStudents) next.add(s.id);
      return next;
    });
  };

  const toggleWeekday = (day: number) => {
    setExcludeWeekdays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));
  };

  const handlePreview = async () => {
    setError(null);
    setStatusMessage(null);
    if (selectedStudents.size === 0) {
      setError("En az bir kursiyer secmelisiniz.");
      return;
    }
    if (!effectiveBranchId) {
      setError(
        isPlatformOwner
          ? "Onizleme icin yukaridan bir sube secmelisiniz."
          : "Hesabiniza bagli sube bulunamadi. Yonetici ile iletisime gecin.",
      );
      return;
    }
    setBusy(true);
    try {
      const response = await previewAutoPlan({
        branchId: effectiveBranchId,
        studentIds: Array.from(selectedStudents),
        startDate,
        endDate,
        lessonsPerStudent,
        maxLessonsPerStudentPerDay: maxPerDay,
        lessonType,
        instructorPriority: priority,
        excludeWeekdays: excludeWeekdays.length > 0 ? excludeWeekdays : undefined,
        preferredInstructorId: preferredInstructorId || undefined,
        preferredVehicleId: preferredVehicleId || undefined,
      });
      setPreviewResult(response);
      setStep("preview");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Önizleme calistirilamadi.");
    } finally {
      setBusy(false);
    }
  };

  const handleCommit = async () => {
    if (!previewResult) return;
    setError(null);
    setStatusMessage(null);
    setBusy(true);
    try {
      const response = await commitAutoPlan(previewResult.run_id);
      setStatusMessage(`Plan Onaylandı. ${response.inserted_count} randevu olusturuldu.`);
      setPreviewResult(null);
      clearDirectoryCache();
      runList.reload();
      setStep("history");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Plan onaylanamadi.");
    } finally {
      setBusy(false);
    }
  };

  const handleRevert = async (runId: string) => {
    const reason = window.prompt("İptal Sebebi (opsiyonel):", "");
    if (reason === null) return;
    setBusy(true);
    setError(null);
    setStatusMessage(null);
    try {
      const response = await revertAutoPlan(runId, reason);
      setStatusMessage(`Plan Geri Alındı. ${response.cancelled_count} randevu İptal Edildi.`);
      clearDirectoryCache();
      runList.reload();
      if (historyDetail?.run_id === runId) {
        const refreshed = await fetchRunDetail(runId);
        setHistoryDetail(refreshed);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Plan geri alinamadi.");
    } finally {
      setBusy(false);
    }
  };

  const handleViewHistoryDetail = async (runId: string) => {
    setBusy(true);
    setError(null);
    try {
      const detail = await fetchRunDetail(runId);
      setHistoryDetail(detail);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Plan detayi Yüklenemedi.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (step !== "preview") setPreviewResult((prev) => prev);
  }, [step]);

  return (
    <div className="kp-autoplan">
      <div className="kp-autoplan__header">
        <div>
          <h1>Otomatik Planlama</h1>
          <p>
            Kursiyerleri sec, parametreleri ayarla, sistem en uyGün program i onerip onayinla
            takvime yerlestirsin. Onaylanan plan istenirse tek tikla geri alinabilir.
          </p>
        </div>
        <div className="kp-autoplan__steps">
          <button
            className={`kp-autoplan__step ${step === "configure" ? "is-active" : ""}`}
            onClick={() => setStep("configure")}
          >
            1. Yapilandir
          </button>
          <button
            className={`kp-autoplan__step ${step === "preview" ? "is-active" : ""}`}
            onClick={() => previewResult && setStep("preview")}
            disabled={!previewResult}
          >
            2. Önizleme
          </button>
          <button
            className={`kp-autoplan__step ${step === "history" ? "is-active" : ""}`}
            onClick={() => setStep("history")}
          >
            3. Gecmis
          </button>
        </div>
      </div>

      {error && <div className="kp-card kp-error">{error}</div>}
      {statusMessage && <div className="kp-card kp-success">{statusMessage}</div>}

      {step === "configure" && (
        <ConfigureStep
          directory={directory}
          isPlatformOwner={isPlatformOwner}
          branchFilter={branchFilter}
          onBranchFilterChange={setBranchFilter}
          branches={branches}
          filteredStudents={filteredStudents}
          studentSearch={studentSearch}
          setStudentSearch={setStudentSearch}
          selectedStudents={selectedStudents}
          toggleStudent={toggleStudent}
          toggleAllVisible={toggleAllVisible}
          selectAllVisible={selectAllVisible}
          cohortFilter={cohortFilter}
          setCohortFilter={setCohortFilter}
          startDate={startDate}
          endDate={endDate}
          lessonsPerStudent={lessonsPerStudent}
          maxPerDay={maxPerDay}
          lessonType={lessonType}
          priority={priority}
          excludeWeekdays={excludeWeekdays}
          preferredInstructorId={preferredInstructorId}
          preferredVehicleId={preferredVehicleId}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          setLessonsPerStudent={setLessonsPerStudent}
          setMaxPerDay={setMaxPerDay}
          setLessonType={setLessonType}
          setPriority={setPriority}
          toggleWeekday={toggleWeekday}
          setPreferredInstructorId={setPreferredInstructorId}
          setPreferredVehicleId={setPreferredVehicleId}
          busy={busy}
          onPreview={handlePreview}
        />
      )}

      {step === "preview" && previewResult && (
        <PreviewStep
          preview={previewResult}
          onCommit={handleCommit}
          onBack={() => setStep("configure")}
          busy={busy}
        />
      )}

      {step === "history" && (
        <HistoryStep
          runList={runList}
          detail={historyDetail}
          busy={busy}
          onView={handleViewHistoryDetail}
          onRevert={handleRevert}
          onClose={() => setHistoryDetail(null)}
        />
      )}
    </div>
  );
}

type ConfigureProps = {
  directory: ReturnType<typeof useDirectoryBundle>;
  isPlatformOwner: boolean;
  branchFilter: string;
  onBranchFilterChange: (branchId: string) => void;
  branches: Array<{ id: string; name: string }>;
  filteredStudents: Array<{ id: string; full_name: string; phone: string | null }>;
  studentSearch: string;
  setStudentSearch: (v: string) => void;
  selectedStudents: Set<string>;
  toggleStudent: (id: string) => void;
  toggleAllVisible: () => void;
  selectAllVisible: () => void;
  cohortFilter: CohortFilterValue;
  setCohortFilter: (v: CohortFilterValue) => void;
  startDate: string;
  endDate: string;
  lessonsPerStudent: number;
  maxPerDay: number;
  lessonType: AutoPlanLessonType;
  priority: AutoPlanInstructorPriority;
  excludeWeekdays: number[];
  preferredInstructorId: string;
  preferredVehicleId: string;
  setStartDate: (v: string) => void;
  setEndDate: (v: string) => void;
  setLessonsPerStudent: (v: number) => void;
  setMaxPerDay: (v: number) => void;
  setLessonType: (v: AutoPlanLessonType) => void;
  setPriority: (v: AutoPlanInstructorPriority) => void;
  toggleWeekday: (day: number) => void;
  setPreferredInstructorId: (v: string) => void;
  setPreferredVehicleId: (v: string) => void;
  busy: boolean;
  onPreview: () => void;
};

function ConfigureStep(props: ConfigureProps) {
  const {
    directory,
    isPlatformOwner,
    branchFilter,
    onBranchFilterChange,
    branches,
    filteredStudents,
    studentSearch,
    setStudentSearch,
    selectedStudents,
    toggleStudent,
    toggleAllVisible,
    selectAllVisible,
    cohortFilter,
    setCohortFilter,
    startDate,
    endDate,
    lessonsPerStudent,
    maxPerDay,
    lessonType,
    priority,
    excludeWeekdays,
    preferredInstructorId,
    preferredVehicleId,
    setStartDate,
    setEndDate,
    setLessonsPerStudent,
    setMaxPerDay,
    setLessonType,
    setPriority,
    toggleWeekday,
    setPreferredInstructorId,
    setPreferredVehicleId,
    busy,
    onPreview,
  } = props;

  return (
    <div className="kp-autoplan__configure">
      <div className="kp-card kp-autoplan__students">
        <div className="kp-autoplan__students-head">
          <h3>Kursiyer Secimi</h3>
          <span className="kp-autoplan__pill">{selectedStudents.size} secili</span>
        </div>
        <CohortFilterBar value={cohortFilter} onChange={setCohortFilter} className="kp-autoplan__cohort" />
        <input
          className="kp-input"
          placeholder="Kursiyer ara (ad veya telefon)"
          value={studentSearch}
          onChange={(e) => setStudentSearch(e.target.value)}
        />
        <div className="kp-autoplan__students-actions">
          <button type="button" className="kp-btn kp-btn--ghost" onClick={toggleAllVisible}>
            Goruneni Sec / Kaldir
          </button>
          {cohortFilter.subgroupId && (
            <button type="button" className="kp-btn kp-btn--ghost" onClick={selectAllVisible}>
              Alt grubun tum adaylarini sec
            </button>
          )}
        </div>
        {directory.loading && <div className="kp-loading">{UI_LOADING}</div>}
        {directory.error && <div className="kp-error">{directory.error}</div>}
        {!directory.loading && !directory.error && (
          <div className="kp-autoplan__student-list">
            {filteredStudents.length === 0 && (
              <div className="kp-empty">Eslesen kursiyer bulunamadi.</div>
            )}
            {filteredStudents.map((s) => (
              <label key={s.id} className="kp-autoplan__student-item">
                <input
                  type="checkbox"
                  checked={selectedStudents.has(s.id)}
                  onChange={() => toggleStudent(s.id)}
                />
                <span className="kp-autoplan__student-name">{s.full_name}</span>
                {s.phone && (
                  <span className="kp-autoplan__student-phone">{formatPhoneDisplay(s.phone)}</span>
                )}
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="kp-card kp-autoplan__params">
        <h3>Plan Parametreleri</h3>
        {isPlatformOwner && (
          <label className="kp-autoplan__branch-field">
            Sube
            <BranchFilterSelect
              value={branchFilter}
              onChange={onBranchFilterChange}
              branches={branches}
              includeAllOption={false}
            />
          </label>
        )}
        <div className="kp-autoplan__form-grid">
          <label>
            Baslangic Tarihi
            <input
              type="date"
              className="kp-input"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </label>
          <label>
            Bitis Tarihi
            <input
              type="date"
              className="kp-input"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>
          <label>
            Kursiyer Basi Ders Sayisi
            <input
              type="number"
              className="kp-input"
              min={1}
              max={60}
              value={lessonsPerStudent}
              onChange={(e) => setLessonsPerStudent(Number(e.target.value))}
            />
          </label>
          <label>
            Gunde Maks. Ders / Kursiyer
            <input
              type="number"
              className="kp-input"
              min={1}
              max={8}
              value={maxPerDay}
              onChange={(e) => setMaxPerDay(Number(e.target.value))}
            />
          </label>
          <label>
            Ders Turu
            <select
              className="kp-input"
              value={lessonType}
              onChange={(e) => setLessonType(e.target.value as AutoPlanLessonType)}
            >
              <option value="normal">Normal</option>
              <option value="special">Ozel</option>
              <option value="extra">Ekstra</option>
            </select>
          </label>
          <label>
            Eğitmen Onceligi
            <select
              className="kp-input"
              value={priority}
              onChange={(e) => setPriority(e.target.value as AutoPlanInstructorPriority)}
            >
              <option value="full_time_first">Tam Zamanli Once</option>
              <option value="balanced">Ad Soyad ile Sirali</option>
            </select>
          </label>
          <label>
            Tercih Edilen Eğitmen (opsiyonel)
            <select
              className="kp-input"
              value={preferredInstructorId}
              onChange={(e) => setPreferredInstructorId(e.target.value)}
            >
              <option value="">— Otomatik —</option>
              {directory.data?.instructors.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.full_name} ({i.employment_type === "full_time" ? "Tam zamanli" : "Gunluk"})
                </option>
              ))}
            </select>
          </label>
          <label>
            Tercih Edilen Araç (opsiyonel)
            <select
              className="kp-input"
              value={preferredVehicleId}
              onChange={(e) => setPreferredVehicleId(e.target.value)}
            >
              <option value="">— Otomatik —</option>
              {directory.data?.vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.plate_no} {v.brand_model ? `· ${v.brand_model}` : ""}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="kp-autoplan__weekdays">
          <span>Plana Dahil Edilmeyecek Gunler:</span>
          {WEEKDAY_LABELS.map((label, idx) => {
            const day = idx + 1;
            const active = excludeWeekdays.includes(day);
            return (
              <button
                type="button"
                key={day}
                className={`kp-autoplan__weekday ${active ? "is-active" : ""}`}
                onClick={() => toggleWeekday(day)}
              >
                {label}
              </button>
            );
          })}
        </div>
        <div className="kp-autoplan__actions">
          <button
            className="kp-btn kp-btn--primary"
            onClick={onPreview}
            disabled={busy || selectedStudents.size === 0}
          >
            {busy ? "Hesaplanıyor..." : "Önizleme Oluştur"}
          </button>
        </div>
      </div>
    </div>
  );
}

type PreviewStepProps = {
  preview: PreviewResponse;
  onCommit: () => void;
  onBack: () => void;
  busy: boolean;
};

function PreviewStep({ preview, onCommit, onBack, busy }: PreviewStepProps) {
  const groupedByStudent = useMemo(() => groupByStudent(preview.planned), [preview.planned]);

  return (
    <div className="kp-autoplan__preview">
      <div className="kp-card">
        <div className="kp-autoplan__summary-row">
          <SummaryBadge label="Toplam istenen" value={preview.summary.total_requested} tone="info" />
          <SummaryBadge label="Planlanan" value={preview.summary.total_planned} tone="ok" />
          <SummaryBadge label="Eksik" value={preview.summary.total_unscheduled} tone="warn" />
          {preview.summary.active_students !== undefined && (
            <SummaryBadge label="Aktif kursiyer" value={preview.summary.active_students} tone="info" />
          )}
          {preview.summary.inactive_students ? (
            <SummaryBadge
              label="Pasif (atlandi)"
              value={preview.summary.inactive_students}
              tone="warn"
            />
          ) : null}
        </div>
        <div className="kp-autoplan__preview-meta">
          <span>Plan #{preview.run_id.slice(0, 8)}</span>
          <span>Oluşturma: {formatIsoDateTime(preview.created_at)}</span>
          <span>Gecerlilik: {formatIsoDateTime(preview.expires_at)}</span>
        </div>
      </div>

      <div className="kp-card">
        <h3>Planlanan Dersler</h3>
        {preview.planned.length === 0 ? (
          <div className="kp-empty">Hiçbir ders planlanamadi. Parametreleri gozden gecirin.</div>
        ) : (
          <div className="kp-autoplan__planned">
            {Array.from(groupedByStudent.entries()).map(([studentName, items]) => (
              <div key={studentName} className="kp-autoplan__student-block">
                <div className="kp-autoplan__student-block-head">
                  <strong>{studentName}</strong>
                  <span>{items.length} ders</span>
                </div>
                <table className="kp-autoplan__table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Tarih / Saat</th>
                      <th>Eğitmen</th>
                      <th>Arac</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((p) => (
                      <tr key={`${p.student_id}-${p.lesson_index}`}>
                        <td>{p.lesson_index}</td>
                        <td>{formatIsoDateTime(p.starts_at)}</td>
                        <td>{p.instructor_name}</td>
                        <td className="mono">{p.plate_no}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>

      {preview.unscheduled.length > 0 && (
        <div className="kp-card">
          <h3>Eksik Kalan Kursiyerler</h3>
          <table className="kp-autoplan__table">
            <thead>
              <tr>
                <th>Kursiyer</th>
                <th>Sebep</th>
                <th>Tamamlanan / Istenen</th>
              </tr>
            </thead>
            <tbody>
              {preview.unscheduled.map((u) => (
                <tr key={u.student_id}>
                  <td>{u.student_name}</td>
                  <td>{REASON_LABELS[u.reason]}</td>
                  <td>
                    {u.scheduled_count} / {u.requested_count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="kp-card kp-autoplan__commit-bar">
        <button className="kp-btn" onClick={onBack} disabled={busy}>
          Parametreleri Düzenle
        </button>
        <button
          className="kp-btn kp-btn--primary"
          onClick={onCommit}
          disabled={busy || preview.summary.total_planned === 0}
          title={preview.summary.total_planned === 0 ? "Planlanacak ders yok" : ""}
        >
          {busy ? "Onaylaniyor..." : "Plani Onayla ve Takvime Yaz"}
        </button>
      </div>
    </div>
  );
}

type HistoryStepProps = {
  runList: ReturnType<typeof useAutoPlanRunList>;
  detail: RunDetail | null;
  busy: boolean;
  onView: (runId: string) => void;
  onRevert: (runId: string) => void;
  onClose: () => void;
};

function HistoryStep({ runList, detail, busy, onView, onRevert, onClose }: HistoryStepProps) {
  return (
    <div className="kp-autoplan__history">
      <div className="kp-card">
        <div className="kp-autoplan__history-head">
          <h3>Plan Gecmisi</h3>
          <button className="kp-btn" onClick={runList.reload} disabled={runList.loading}>
            Yenile
          </button>
        </div>
        {runList.loading && <div className="kp-loading">{UI_LOADING}</div>}
        {runList.error && <div className="kp-error">{runList.error}</div>}
        {!runList.loading && !runList.error && (
          <table className="kp-autoplan__table">
            <thead>
              <tr>
                <th>Tarih</th>
                <th>Durum</th>
                <th>Istenen / Planlanan</th>
                <th>Eklenen / İptal</th>
                <th aria-label="Aksiyon"></th>
              </tr>
            </thead>
            <tbody>
              {runList.data?.items.length === 0 && (
                <tr>
                  <td colSpan={5} className="kp-empty">
                    Henuz plan calistirilmamis.
                  </td>
                </tr>
              )}
              {runList.data?.items.map((r) => (
                <tr key={r.run_id}>
                  <td>{formatIsoDateTime(r.created_at)}</td>
                  <td>
                    <span className={`kp-autoplan__status kp-autoplan__status--${r.status}`}>
                      {labelForStatus(r.status)}
                    </span>
                  </td>
                  <td>
                    {r.total_requested} / {r.total_planned}
                  </td>
                  <td>
                    {r.inserted_count}
                    {r.cancelled_count > 0 ? ` / -${r.cancelled_count}` : ""}
                  </td>
                  <td>
                    <div className="kp-autoplan__row-actions">
                      <button className="kp-btn" onClick={() => onView(r.run_id)} disabled={busy}>
                        Detay
                      </button>
                      {r.status === "committed" && (
                        <button
                          className="kp-btn kp-btn--ghost"
                          onClick={() => onRevert(r.run_id)}
                          disabled={busy}
                        >
                          Geri Al
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detail && (
        <div className="kp-card">
          <div className="kp-autoplan__history-head">
            <h3>Plan Detayi #{detail.run_id.slice(0, 8)}</h3>
            <button className="kp-btn" onClick={onClose}>
              Kapat
            </button>
          </div>
          <div className="kp-autoplan__summary-row">
            <SummaryBadge label="Durum" value={labelForStatus(detail.status)} tone="info" />
            <SummaryBadge
              label="Planlanan"
              value={detail.summary.total_planned}
              tone="ok"
            />
            <SummaryBadge label="Eksik" value={detail.summary.total_unscheduled} tone="warn" />
            {detail.summary.inserted_count !== undefined && (
              <SummaryBadge label="Eklenen" value={detail.summary.inserted_count} tone="ok" />
            )}
            {detail.summary.cancelled_count ? (
              <SummaryBadge
                label="İptal Edilen"
                value={detail.summary.cancelled_count}
                tone="warn"
              />
            ) : null}
          </div>
          <div className="kp-autoplan__preview-meta">
            <span>Oluşturma: {formatIsoDateTime(detail.created_at)}</span>
            {detail.committed_at && <span>Onay: {formatIsoDateTime(detail.committed_at)}</span>}
            {detail.reverted_at && <span>İptal: {formatIsoDateTime(detail.reverted_at)}</span>}
          </div>
          <div className="kp-autoplan__planned">
            {Array.from(groupByStudent(detail.planned).entries()).map(([studentName, items]) => (
              <div key={studentName} className="kp-autoplan__student-block">
                <div className="kp-autoplan__student-block-head">
                  <strong>{studentName}</strong>
                  <span>{items.length} ders</span>
                </div>
                <ul className="kp-autoplan__lesson-list">
                  {items.map((p) => (
                    <li key={`${p.student_id}-${p.lesson_index}`}>
                      <span>{formatIsoDate(p.date)}</span>
                      <span>{formatIsoDateTime(p.starts_at)}</span>
                      <span>{p.instructor_name}</span>
                      <span className="mono">{p.plate_no}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryBadge({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string;
  tone: "info" | "ok" | "warn";
}) {
  return (
    <div className={`kp-autoplan__badge kp-autoplan__badge--${tone}`}>
      <span className="kp-autoplan__badge-label">{label}</span>
      <span className="kp-autoplan__badge-value">{value}</span>
    </div>
  );
}

function groupByStudent(planned: PlannedAppointment[]): Map<string, PlannedAppointment[]> {
  const map = new Map<string, PlannedAppointment[]>();
  for (const p of planned) {
    const list = map.get(p.student_name) || [];
    list.push(p);
    map.set(p.student_name, list);
  }
  for (const list of map.values()) {
    list.sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime());
  }
  return map;
}

function labelForStatus(status: string): string {
  switch (status) {
    case "preview":
      return "Önizleme";
    case "committed":
      return "Onaylandi";
    case "reverted":
      return "Geri Alindi";
    case "failed":
      return "Hata";
    default:
      return status;
  }
}
